import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { client } from '../client';

function unwrapApiResponse(data: any): any {
  if (data && typeof data === 'object' && 'success' in data && 'data' in data) return data.data;
  return data;
}

function normalizeProjectEntity(project: any) {
  if (!project || typeof project !== 'object') return project;
  const normalized = { ...project };
  if (normalized.companyId === undefined && normalized.customerId !== undefined) {
    normalized.companyId = normalized.customerId;
  }
  if (normalized.company === undefined && normalized.Customer?.name) {
    normalized.company = normalized.Customer.name;
  }
  return normalized;
}

function normalizeProjectList(projects: any[]) {
  if (!Array.isArray(projects)) return [];
  return projects.map(normalizeProjectEntity);
}

export const useProjects = (workspaceId = '') => {
  return useQuery({
    queryKey: ['projects', workspaceId],
    queryFn: () => client.get('/projects', { params: { workspaceId } }).then(r => r.data).then(unwrapApiResponse).then(normalizeProjectList),
    enabled: !!workspaceId,
    staleTime: 1000 * 60 * 5,
  });
};

export const useProject = (id: string, workspaceId: string) => {
  return useQuery({
    queryKey: ['project', id, workspaceId],
    queryFn: () => client.get(`/projects/${id}`, { params: { workspaceId } }).then(r => r.data).then(unwrapApiResponse),
    enabled: !!id && !!workspaceId,
  });
};

export const useTasks = (workspaceId = '') => {
  return useQuery({
    queryKey: ['tasks', workspaceId],
    queryFn: () => client.get('/tasks', { params: { workspaceId } }).then(r => r.data).then(unwrapApiResponse),
    enabled: !!workspaceId,
    staleTime: 1000 * 60 * 5,
  });
};

export const useDocuments = (workspaceId = '') => {
  return useQuery({
    queryKey: ['documents', workspaceId],
    queryFn: () => client.get('/documents', { params: workspaceId ? { workspaceId } : undefined }).then(r => r.data).then(unwrapApiResponse),
    enabled: !!workspaceId,
    staleTime: 1000 * 30,
    refetchOnWindowFocus: true,
    retry: (failureCount, error: any) => {
      const status = error?.status || error?.response?.status;
      return status === 429 ? failureCount < 4 : failureCount < 2;
    },
    retryDelay: (attemptIndex, error: any) => {
      const status = error?.status || error?.response?.status;
      return status === 429 ? Math.min(1000 * 2 ** attemptIndex, 10000) : 1000;
    },
  });
};

export const useIdeas = (workspaceId = '') => {
  return useQuery({
    queryKey: ['ideas', workspaceId],
    queryFn: () => client.get('/ideas', { params: { workspaceId } }).then(r => r.data).then(unwrapApiResponse),
    enabled: !!workspaceId,
    staleTime: 1000 * 60 * 5,
  });
};

export const useProducts = (workspaceId = '') => {
  return useQuery({
    queryKey: ['products', workspaceId],
    queryFn: () => client.get('/products', { params: { workspaceId } }).then(r => r.data).then(unwrapApiResponse),
    enabled: !!workspaceId,
    staleTime: 1000 * 60 * 5,
  });
};

export const useCreateProject = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: any) => client.post('/projects', data).then(r => r.data),
    onSuccess: (_data, vars: any) => qc.invalidateQueries({ queryKey: ['projects', vars.workspaceId] }),
  });
};

export const useUpdateProject = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...data }: any) => client.put(`/projects/${id}`, data).then(r => r.data),
    onSuccess: (_data, vars: any) => {
      qc.invalidateQueries({ queryKey: ['projects', vars.workspaceId] });
      qc.invalidateQueries({ queryKey: ['project', vars.id] });
    },
  });
};

export const useDeleteProject = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => client.delete(`/projects/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['projects'] }),
  });
};
